import React, {useEffect, useRef, useState} from 'react';
import {initializeApp} from 'firebase/app';
import {getMessaging, getToken, onMessage} from 'firebase/messaging';
import {useChatsStore} from '../../redux/chats';
import {useAuthStore} from '../../redux/auth';
import {getDeviceUuid} from '../../utils/deviceUuid';
import {handleNotificationAction} from "../common/pushListenerHandler";
import {useCustomerStore} from "../../redux/customers";
import {handleDeepLinkUrl} from "../common/deepLinkHandler";
import {handleReminderForeground} from "../common/reminderForegroundHandler";
import {useToastStore} from "../../redux/toast";

// A handled chat deep link short-circuits the tap path before
// handleNotificationAction runs, so the pushed message itself must still be
// ingested or the thread renders stale (the store dedupes by id, and messages
// carry their own customerId, so this is safe for any customer).
const ingestChatMessage = (data: any, addMessage: (message: any) => void) => {
    if (data?.type !== 'chatMessage' || !data.chatMessage) return;
    try {
        const parsedMessage = JSON.parse(data.chatMessage);
        if (parsedMessage?.customerId) {
            addMessage(parsedMessage);
        }
    } catch (error) {
        console.error('Failed to parse chatMessage from notification tap:', error);
    }
};

const WebPushListener: React.FC = () => {
    const { addMessage } = useChatsStore();
    const { registerFcmToken } = useAuthStore();
    const { show: showToast } = useToastStore();

    const customerStore = useCustomerStore();
    const {setSelectedId: setSelectedCustomerId, customers, activeCustomer} = customerStore;

    // Use refs for dynamic state to avoid closure staleness in listeners
    const customersRef = useRef(customers);
    const activeCustomerRef = useRef(activeCustomer);
    const setSelectedCustomerIdRef = useRef(setSelectedCustomerId);
    const addMessageRef = useRef(addMessage);
    const showToastRef = useRef(showToast);

    const [pendingNotification, setPendingNotification] = useState<any | null>(null);

    const [firebaseConfig, setFirebaseConfig] = useState<any>(null);
    const [messaging, setMessaging] = useState<any>(null);
    const [deviceUuid, setDeviceUuid] = useState<string | null>(null);

    useEffect(() => {
        customersRef.current = customers;
    }, [customers]);

    useEffect(() => {
        activeCustomerRef.current = activeCustomer;
    }, [activeCustomer]);

    useEffect(() => {
        setSelectedCustomerIdRef.current = setSelectedCustomerId;
    }, [setSelectedCustomerId]);

    useEffect(() => {
        addMessageRef.current = addMessage;
    }, [addMessage]);

    useEffect(() => {
        showToastRef.current = showToast;
    }, [showToast]);

    // Handle processing pending notification once customers are available
    useEffect(() => {
        if (pendingNotification && customers && customers.length > 0) {
            const payload = pendingNotification;
            setPendingNotification(null);
            console.log('Processing pending web notification action with loaded customers:', payload);

            (async () => {
                try {
                    const currentLocation = window?.location?.pathname;

                    if (payload.data.deepLinkUrl) {
                        const handled = await handleDeepLinkUrl({
                            url: payload.data.deepLinkUrl,
                            customers: customersRef.current,
                            setSelectedCustomerId: setSelectedCustomerIdRef.current,
                            history: {
                                push: (url: string) => window.location.assign(url),
                            },
                            currentLocation,
                        });

                        if (handled) {
                            ingestChatMessage(payload.data, addMessageRef.current);
                            return;
                        }
                    }

                    if (payload.data.type) {
                        await handleNotificationAction({
                            data: payload.data,
                            customers: customersRef.current,
                            setSelectedCustomerId: setSelectedCustomerIdRef.current,
                            history: { push: (url: string) => window.location.assign(url) },
                            currentLocation,
                            addMessage: addMessageRef.current,
                        });
                    }
                } catch (error) {
                    console.error('Error handling pending service worker message:', error, payload);
                }
            })();
        }
    }, [pendingNotification, customers]);

    // Resolve the shared device identity — the same source LogoutHandler sends
    // to /auth/logout, so the API can match and delete this browser's token row.
    useEffect(() => {
        getDeviceUuid().then(setDeviceUuid);

        fetch('/web-config.json')
            .then((res) => res.json())
            .then((config) => {
                if (!config?.firebase) {
                    throw new Error('Missing firebase config in web-config.json');
                }
                setFirebaseConfig(config.firebase);
                const firebaseApp = initializeApp(config.firebase);
                const messagingInstance = getMessaging(firebaseApp);
                setMessaging(messagingInstance);
            })
            .catch((error) => {
                console.error('Error loading web-config.json:', error);
            });
    }, []);

    // Request permission and set up token and foreground messaging
    useEffect(() => {
        if (!firebaseConfig || !messaging || !deviceUuid) return;

        const requestPermissionAndToken = async () => {
            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    const swRegistration = await navigator.serviceWorker.ready;
                    const token = await getToken(messaging, {
                        vapidKey: firebaseConfig.vapidKey,
                        serviceWorkerRegistration: swRegistration,
                    });

                    if (token) {
                        await registerFcmToken({ deviceUuid, fcmToken: token, platform: 'web' });
                    } else {
                        console.warn('No web push token was retrieved.');
                    }
                } else {
                    console.warn('Notification permission not granted.');
                }
            } catch (error) {
                console.error('Error requesting web push permission:', error);
            }
        };

        requestPermissionAndToken();

        // Foreground push messages
        const unsubscribe = onMessage(messaging, (payload) => {
            console.log('Foreground web push message:', payload);

            const data = payload.data ?? {};
            switch (data.type) {
                case 'chatMessage': {
                    try {
                        const parsedMessage = JSON.parse(data.chatMessage);
                        console.log('Parsed ChatMessage:', parsedMessage);
                        if (parsedMessage?.customerId) {
                            if (activeCustomerRef.current?.id === parsedMessage.customerId) {
                                addMessageRef.current(parsedMessage);
                            } else {
                                console.warn('Message received via web push but customer is not active. Skipping auto-update.');
                            }
                        }
                    } catch (err) {
                        console.error('Error processing web push message:', err);
                    }
                    break;
                }
                case 'shopPaymentComplete':
                    console.log('Shop payment complete web push message received in foreground');
                    break;
                case 'eventReminder': {
                    const eventId = data.eventId;
                    const customerId = data.customerId
                        ? parseInt(data.customerId as string, 10)
                        : null;
                    const knownCustomer = customerId != null
                        && customersRef.current?.some((c) => c.id === customerId);

                    const navigate = eventId
                        ? async () => {
                            if (knownCustomer && activeCustomerRef.current?.id !== customerId) {
                                await setSelectedCustomerIdRef.current({id: customerId as number});
                            }
                            window.location.assign(`/events/${eventId}`);
                        }
                        : null;

                    handleReminderForeground({
                        notification: {
                            title: payload.notification?.title,
                            body: payload.notification?.body,
                        },
                        showToast: showToastRef.current,
                        navigate,
                    }).catch((err) => console.error('Reminder foreground toast failed:', err));
                    break;
                }
                case 'bookingReminder': {
                    handleReminderForeground({
                        notification: {
                            title: payload.notification?.title,
                            body: payload.notification?.body,
                        },
                        showToast: showToastRef.current,
                        navigate: null,
                    }).catch((err) => console.error('Reminder foreground toast failed:', err));
                    break;
                }
                default:
                    console.warn('Unknown notification type:', data.type);
            }
        });

        return () => unsubscribe();
    }, [firebaseConfig, messaging, deviceUuid, registerFcmToken]);

    // Listen for messages forwarded from the service worker and handle actions
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            const messageHandler = async (event: MessageEvent) => {
                const payload = event.data;

                // The SW only forwards notification taps (F8): anything else —
                // e.g. an outdated SW still broadcasting on push *receipt* —
                // must not be treated as a tap and navigate the tab.
                if (payload?.type !== 'NOTIFICATION_CLICK' || !payload.data) return;
                console.log('Received notification click from SW:', payload);

                // Check if customers are loaded before processing
                if (!customersRef.current || customersRef.current.length === 0) {
                    console.log('Service worker message received but customers not yet loaded. Queuing.');
                    setPendingNotification(payload);
                    return;
                }

                try {
                    const currentLocation = window?.location?.pathname;

                    if (payload.data.deepLinkUrl) {
                        const handled = await handleDeepLinkUrl({
                            url: payload.data.deepLinkUrl,
                            customers: customersRef.current,
                            setSelectedCustomerId: setSelectedCustomerIdRef.current,
                            history: {
                                push: (url: string) => window.location.assign(url),
                            },
                            currentLocation,
                        });

                        if (handled) {
                            ingestChatMessage(payload.data, addMessageRef.current);
                            return;
                        }
                    }

                    if (payload.data.type) {
                        await handleNotificationAction({
                            data: payload.data,
                            customers: customersRef.current,
                            setSelectedCustomerId: setSelectedCustomerIdRef.current,
                            history: { push: (url: string) => window.location.assign(url) },
                            currentLocation,
                            addMessage: addMessageRef.current,
                        });
                    }
                } catch (error) {
                    console.error('Error handling service worker message:', error, payload);
                }
            };

            navigator.serviceWorker.addEventListener('message', messageHandler);

            return () => {
                navigator.serviceWorker.removeEventListener('message', messageHandler);
            };
        }
    }, []);

    return null;
};

export default WebPushListener;
